// Making a hike or a recording public so a shared link opens for everyone — the one path both the
// "make public & share" link modal (CopyLinkModal.vue) and the video share wizard's privacy prompt
// (FlyoverShare.vue) go through, so the two can't disagree on which mutation or field publishes.

import graph_client from './graphql/graph_client';
import { UpdateHikeDetails } from './graphql/hikes-graph';
import { UpdateRecording } from './graphql/recorded-trail-graph';
import { i18n } from '../i18n';

const t = (key: string): string => i18n.global.t(key);

export type PublishableSubject = 'hike' | 'recording';

/**
 * Publish the subject. Throws with a user-facing message when the server refuses — UpdateHikeDetails
 * reports validation failures as `errors` on a successful response rather than as a GraphQL error.
 */
export async function makeSubjectPublic(type: PublishableSubject, id: string | number): Promise<void> {
  if (type === 'hike') {
    const result: any = await graph_client.request(UpdateHikeDetails, {
      input: { id, publiclyVisible: true },
    });
    const errors = result?.updateHikeDetails?.errors;
    if (errors && errors.length > 0) {
      const msgs = errors.flatMap((e: any) => e.errors || []);
      throw new Error(msgs.join(', ') || t('utils.copy_link_modal.failed_to_update_hike'));
    }
    return;
  }
  await graph_client.request(UpdateRecording, { input: { id, public: true } });
}

/** True when the signed-in user may publish: the server keeps an unconfirmed account's hikes private. */
export function viewerEmailConfirmed(user: { confirmed_at?: string | null; [key: string]: any } | null | undefined = window.user): boolean {
  return !!user && !!user.confirmed_at;
}

/** Resend the confirmation email; resolves to the status line to show. */
export async function resendConfirmationEmail(): Promise<string> {
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const response = await fetch('/user/resend_confirmation', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken || '', Accept: 'application/json' },
    });
    const data = await response.json();
    return data?.message || t('utils.copy_link_modal.confirmation_email_sent');
  } catch {
    return t('utils.copy_link_modal.could_not_send_confirmation_email');
  }
}
